/**
 * Single reporter for every NATS connect-path failure.
 *
 * Three sinks on every call, in this order:
 *   1. `console.error` — one human-readable line plus the structured object.
 *      Deliberately NOT gated on `NODE_ENV`: an end user's production console
 *      (and the RUM console capture behind it) must say exactly why realtime
 *      is down, on every attempt.
 *   2. The `sdkEvents` bus (`error` channel) → OTLP translator, sampled by
 *      `shouldSample` (the NATS codes below are pinned at 100 %).
 *   3. The host sink installed by `SDKProvider` (`onConnectError` prop) —
 *      web hosts forward it to their RUM logger.
 *
 * Sinks 2 and 3 fire on attempts 1, 2, 4, 8, … and on recovery so a client
 * stuck in a 30 s ladder stays under one telemetry event per minute; the
 * console line fires on every attempt. Nothing here is user-visible UI.
 *
 * Platform-agnostic: `globalThis` pin only (HMR-safe), no web/RN API.
 */

import { sdkEvents } from '../../core/events.js';

/** What triggered the attempt that failed. */
export type NatsFailurePhase =
  | 'bootstrap'
  | 'identity-flip'
  | 'retry'
  | 'closed'
  | 'resume'
  | 'jwt-refresh'
  | 'leak';

/** Which step of the connect path failed. */
export type NatsFailureStage =
  | 'mint'
  | 'ws-connect'
  | 'jetstream-bootstrap'
  | 'filter-update'
  | 'consumer-recovery'
  | 'jwt-refresh'
  | 'connection-count';

export type NatsFailureCode =
  | 'NATS_CONNECT_FAILED'
  | 'NATS_MINT_FAILED'
  | 'NATS_WIRE_BOOTSTRAP_FAILED'
  | 'NATS_FILTER_UPDATE_FAILED'
  | 'NATS_CONNECTION_CLOSED'
  | 'NATS_JWT_REFRESH_FAILED'
  | 'NATS_CONNECTION_LEAK';

export type NatsRecoveryCode =
  | 'NATS_CONNECT_RECOVERED'
  | 'NATS_WIRE_RECOVERED'
  | 'NATS_FILTER_RECONCILE_RECOVERED'
  | 'NATS_CONNECTION_LEAK_RESOLVED';

export interface NatsFailureReport {
  readonly phase: NatsFailurePhase;
  readonly stage: NatsFailureStage;
  readonly code: NatsFailureCode;
  /** Short classified cause: `auth-rejected`, `timeout`, `consumer_not_found`, … */
  readonly reason: string;
  /** Underlying error message, string-coerced. */
  readonly message: string;
  /** The broker or backend refused the credentials (vs a transport/timeout failure). */
  readonly isAuth: boolean;
  /** 1-based attempt inside the current retry loop; absent for a one-off failure. */
  readonly attempt?: number;
  /** Delay until the next automatic retry; `null` when this loop schedules none. */
  readonly nextRetryMs?: number | null;
  readonly natsUrl?: string;
  /** Subject root, consumer or JetStream call the failure concerns. */
  readonly subject?: string;
  readonly error?: unknown;
}

export interface NatsRecoveryReport {
  readonly stage: NatsFailureStage;
  readonly code: NatsRecoveryCode;
  readonly attempts: number;
  readonly downtimeMs: number;
  readonly subject?: string;
}

export type NatsFailureSink = (report: NatsFailureReport) => void;

const SINK_PIN_KEY = '__morfi_natsFailureSink' as const;

type GlobalWithSink = typeof globalThis & {
  [SINK_PIN_KEY]?: NatsFailureSink | null;
};

/** Install the host sink (SDKProvider). Returns an uninstaller. Idempotent. */
export function setNatsFailureSink(sink: NatsFailureSink | null): () => void {
  const g = globalThis as GlobalWithSink;
  g[SINK_PIN_KEY] = sink;
  return (): void => {
    if (g[SINK_PIN_KEY] === sink) g[SINK_PIN_KEY] = null;
  };
}

/**
 * Attempts 1, 2, 4, 8, 16, … (and one-off failures with no attempt number)
 * reach telemetry and the host sink; the console line is unconditional.
 */
export function shouldForwardNatsFailure(attempt: number | undefined): boolean {
  if (attempt === undefined || attempt <= 2) return true;
  return (attempt & (attempt - 1)) === 0;
}

const AUTH_PATTERN =
  /authorization violation|authorizationerror|not authorized|authentication error|permissions violation|invalid (?:credentials|client certificate)|user authentication expired/i;

/**
 * The broker lost a consumer's on-disk store (`meta.inf.tmp: no such file or
 * directory`): every `consumers.update` on it fails until it is replaced.
 */
const CONSUMER_STORE_ERROR_PATTERN = /no such file or directory|meta\.inf/i;

export function isConsumerStoreError(err: unknown): boolean {
  return CONSUMER_STORE_ERROR_PATTERN.test(describeError(err));
}

/** Human-readable `name: message` for any thrown value. */
export function describeError(err: unknown): string {
  if (err instanceof Error) {
    const api = (err as { api_error?: { err_code?: number; description?: string } }).api_error;
    const apiSuffix =
      api !== undefined && typeof api.err_code === 'number'
        ? ` [js:${api.err_code}${api.description !== undefined ? ` ${api.description}` : ''}]`
        : '';
    return `${err.name}: ${err.message}${apiSuffix}`;
  }
  if (typeof err === 'string') return err;
  return String(err);
}

/**
 * Short classified cause for a thrown value. Order matters: the specific
 * shapes (SdkError category, JetStream API code, nats-core error names)
 * come before the message regexes.
 */
export function classifyFailureReason(err: unknown): {
  readonly reason: string;
  readonly isAuth: boolean;
} {
  const obj = (err ?? null) as {
    readonly name?: unknown;
    readonly message?: unknown;
    readonly category?: unknown;
    readonly code?: unknown;
    readonly httpStatus?: unknown;
    readonly api_error?: { readonly err_code?: unknown };
  } | null;
  if (obj !== null && typeof obj === 'object') {
    if (obj.category === 'auth') return { reason: 'auth-rejected', isAuth: true };
    if (typeof obj.httpStatus === 'number') {
      return {
        reason: `http-${obj.httpStatus}`,
        isAuth: obj.httpStatus === 401 || obj.httpStatus === 403,
      };
    }
    // nats.js v3 `JetStreamApiError` carries the API code as `.code`; the
    // legacy shape nested it under `api_error`.
    const apiCode =
      obj.api_error?.err_code ?? (obj.name === 'JetStreamApiError' ? obj.code : undefined);
    if (typeof apiCode === 'number') {
      if (apiCode === 10014) return { reason: 'consumer_not_found', isAuth: false };
      if (isConsumerStoreError(err)) return { reason: 'consumer-store-missing', isAuth: false };
      return { reason: `jetstream-api-${apiCode}`, isAuth: false };
    }
    const name = typeof obj.name === 'string' ? obj.name : '';
    if (name === 'HandshakeTimeoutError') return { reason: 'handshake-timeout', isAuth: false };
    if (name === 'TimeoutError' || name === 'RequestTimeoutError') {
      return { reason: 'timeout', isAuth: false };
    }
    if (name === 'AbortError') return { reason: 'timeout', isAuth: false };
    if (name === 'ClosedConnectionError' || name === 'ConnectionClosedError') {
      return { reason: 'connection-closed', isAuth: false };
    }
    if (name === 'ConsumerNotFoundError') return { reason: 'consumer_not_found', isAuth: false };
  }
  const message = describeError(err);
  if (CONSUMER_STORE_ERROR_PATTERN.test(message)) {
    return { reason: 'consumer-store-missing', isAuth: false };
  }
  if (AUTH_PATTERN.test(message)) return { reason: 'auth-rejected', isAuth: true };
  if (/timed? ?out|timeout/i.test(message)) return { reason: 'timeout', isAuth: false };
  if (/failed to fetch|networkerror|network request failed|load failed/i.test(message)) {
    return { reason: 'network', isAuth: false };
  }
  if (/econnrefused|connection refused|websocket/i.test(message)) {
    return { reason: 'wsconnect-reject', isAuth: false };
  }
  return { reason: 'unknown', isAuth: false };
}

function formatDelay(ms: number | null | undefined): string {
  if (ms === null) return 'no automatic retry';
  if (ms === undefined) return '';
  return `next retry in ${(ms / 1000).toFixed(1)}s`;
}

export function formatNatsFailureLine(report: NatsFailureReport): string {
  const parts: string[] = [report.phase];
  if (report.attempt !== undefined) parts.push(`attempt ${report.attempt}`);
  const delay = formatDelay(report.nextRetryMs);
  if (delay !== '') parts.push(delay);
  const where = report.subject !== undefined ? ` @ ${report.subject}` : '';
  return `[morfi-sdk] NATS ${report.stage} failed (${parts.join(', ')})${where}: ${report.reason} — ${report.message}`;
}

/** Report one failed attempt. Console on every call; bus + host sink per `shouldForwardNatsFailure`. */
export function reportNatsFailure(report: NatsFailureReport): void {
  const line = formatNatsFailureLine(report);
  console.error(line, {
    phase: report.phase,
    stage: report.stage,
    code: report.code,
    reason: report.reason,
    attempt: report.attempt ?? null,
    nextRetryMs: report.nextRetryMs ?? null,
    isAuth: report.isAuth,
    natsUrl: report.natsUrl ?? null,
    subject: report.subject ?? null,
    error: report.error,
  });
  if (!shouldForwardNatsFailure(report.attempt)) return;
  sdkEvents.emit('error', {
    category: report.isAuth ? 'auth' : 'realtime-degraded',
    code: report.code,
    devMessage: line,
    subject: report.subject ?? report.natsUrl,
    phase: report.phase,
    stage: report.stage,
    reason: report.reason,
    attempt: report.attempt,
    nextRetryMs: report.nextRetryMs,
    isAuth: report.isAuth,
    cause: report.error,
  });
  try {
    (globalThis as GlobalWithSink)[SINK_PIN_KEY]?.(report);
  } catch (sinkErr) {
    console.error('[morfi-sdk] NATS failure sink threw:', sinkErr);
  }
}

/** Report a loop that finally succeeded. Console line + bus `recovery` event. */
export function reportNatsRecovery(report: NatsRecoveryReport): void {
  const where = report.subject !== undefined ? ` @ ${report.subject}` : '';
  console.warn(
    `[morfi-sdk] NATS ${report.stage} recovered after ${report.attempts} attempt(s), ${(report.downtimeMs / 1000).toFixed(1)}s down${where}`,
  );
  sdkEvents.emit('recovery', {
    category: 'realtime-degraded',
    code: report.code,
    stage: report.stage,
    attempts: report.attempts,
    downtimeMs: report.downtimeMs,
    subject: report.subject,
  });
}
